import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button, Card, Spin, Flex, Alert, Modal, Divider, message, Tooltip, Badge, Tabs, Upload } from 'antd';
import { ArrowLeftOutlined, UndoOutlined, DeleteOutlined, PlusOutlined, SendOutlined, UploadOutlined, DownloadOutlined, LockOutlined } from '@ant-design/icons';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { useNavigate, useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import SignatureCanvas from 'react-signature-canvas';
import { Rnd } from 'react-rnd';
import * as actions from "store/actions/AuthActions";
import { notify } from "components";
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const MAX_RETRIES = 3;

// SignatureField component with react-rnd for draggable/resizable functionality
const SignatureField = ({ field, scaleFactor, onUpdate, onRemove, onClick, isActive, disabled }) => {
  const screenX = field.x * scaleFactor;
  const screenY = field.y * scaleFactor;
  const screenWidth = field.width * scaleFactor;
  const screenHeight = field.height * scaleFactor;
  const isLocked = field.isLocked;

  // Determine border color based on state
  const getBorderStyle = () => {
    if (isLocked) return '2px solid #722ed1'; // Purple for locked/existing signatures
    if (isActive) return '2px solid #1890ff';
    if (field.signatureData) return '2px solid #52c41a';
    return '2px dashed #999';
  };

  return (
    <Rnd
      bounds="parent"
      size={{ width: screenWidth, height: screenHeight }}
      position={{ x: screenX, y: screenY }}
      disableDragging={disabled || isLocked}
      enableResizing={!disabled && !isLocked}
      onDragStop={(e, d) => {
        if (!disabled && !isLocked) {
          onUpdate({
            ...field,
            x: d.x / scaleFactor,
            y: d.y / scaleFactor
          });
        }
      }}
      onResizeStop={(e, direction, ref, delta, position) => {
        if (!disabled && !isLocked) {
          onUpdate({
            ...field,
            x: position.x / scaleFactor,
            y: position.y / scaleFactor,
            width: ref.offsetWidth / scaleFactor,
            height: ref.offsetHeight / scaleFactor
          });
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!isLocked) onClick();
      }}
      style={{
        border: getBorderStyle(),
        borderRadius: 4,
        background: field.signatureData ? 'rgba(255, 255, 255, 0.95)' : 'rgba(24, 144, 255, 0.1)',
        cursor: isLocked ? 'default' : (disabled ? 'default' : 'move'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}
    >
      {field.signatureData ? (
        <img
          src={field.signatureData}
          alt="Signature"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            pointerEvents: 'none'
          }}
        />
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          fontSize: 12,
          color: '#666',
          textAlign: 'center',
          padding: 4
        }}>
          <PlusOutlined style={{ fontSize: 16, marginBottom: 4 }} />
          <span>Click to sign</span>
        </div>
      )}
      {/* Lock indicator for existing signatures */}
      {isLocked && (
        <Tooltip title="Existing signature (locked)">
          <div
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: '#722ed1',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              zIndex: 10
            }}
          >
            <LockOutlined />
          </div>
        </Tooltip>
      )}
      {/* Remove button for new signatures only */}
      {!disabled && !isLocked && (
        <Tooltip title="Remove signature">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove(field.id);
            }}
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              width: 20,
              height: 20,
              borderRadius: '50%',
              border: 'none',
              background: '#ff4d4f',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              zIndex: 10
            }}
          >
            <DeleteOutlined />
          </button>
        </Tooltip>
      )}
    </Rnd>
  );
};

const DocumentSigningPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const sigCanvasRef = useRef(null);
  const pdfContainerRef = useRef(null);

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [document, setDocument] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
  const [alreadySignedModalShown, setAlreadySignedModalShown] = useState(false);
  const [, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null);

  // Signature input mode state
  const [signatureMode, setSignatureMode] = useState('draw');
  const [uploadedSignaturePreview, setUploadedSignaturePreview] = useState(null);

  // Multi-signature state - separate existing (locked) from new (editable) signatures
  const [existingSignatures, setExistingSignatures] = useState([]);
  const [newSignatureFields, setNewSignatureFields] = useState([]);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [scaleFactor, setScaleFactor] = useState(1);

  // Combine existing and new signatures for display
  const allSignatureFields = useMemo(() => {
    return [...existingSignatures, ...newSignatureFields];
  }, [existingSignatures, newSignatureFields]);

  // Calculate scale factor - react-pdf renders at scale={zoomLevel},
  // so 1 PDF point = zoomLevel CSS pixels
  useEffect(() => {
    setScaleFactor(zoomLevel);
  }, [zoomLevel]);

  // Fetch document data
  const fetchDocument = useCallback(async () => {
    setLoading(true);
    setPdfUrl(null);
    setNumPages(0);
    setPdfLoading(true);
    setPdfError(null);
    setNewSignatureFields([]);
    setExistingSignatures([]);
    try {
      const response = await actions.loadDocument(id);
      if (response) {
        setDocument(response);

        // Load existing signatures if present in API response
        if (response.signatures && Array.isArray(response.signatures)) {
          const existingSigs = response.signatures.map((sig, index) => ({
            id: `existing_${sig.id || index}`,
            x: sig.position_x || 0,
            y: sig.position_y || 0,
            width: sig.width || 150,
            height: sig.height || 50,
            page: sig.page || 1,
            signatureData: sig.signature_image || sig.signature_image_as_base64 || null,
            signerName: sig.signer_name || '',
            isLocked: true  // Mark as non-editable
          }));
          setExistingSignatures(existingSigs);
        }

        // Use signed document if available, otherwise fall back to original
        // This ensures consistency with "View Document" action
        if (response.is_signed && response.signed_document_file) {
          setPdfUrl(response.signed_document_file);
        } else if (response.document_file) {
          setPdfUrl(response.document_file);
        }
      }
    } catch (error) {
      console.error("Error fetching document:", error);
      message.error("Failed to load document");
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  // Show warning modal if document is already signed
  useEffect(() => {
    if (document?.is_signed && !alreadySignedModalShown) {
      setAlreadySignedModalShown(true);
      Modal.confirm({
        title: 'Document Already Signed',
        content: `This document was signed${document.signed_at ? ` on ${new Date(document.signed_at).toLocaleString()}` : ''}${document.signer_name ? ` by ${document.signer_name}` : ''}. Do you want to view it anyway?`,
        okText: 'View Document',
        cancelText: 'Go Back',
        onCancel: () => navigate('/documents'),
      });
    }
  }, [document, alreadySignedModalShown, navigate]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPdfError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error("PDF load error:", error);
    setPdfLoading(false);
    setPdfError(error);
    message.error("Failed to load PDF document");
  };

  // Handle click on PDF page to add new signature field
  const handlePdfClick = (e, pageNumber) => {
    const pageElement = e.currentTarget;
    const rect = pageElement.getBoundingClientRect();

    // Calculate click position relative to page
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Convert to PDF coordinates
    const effectiveScaleFactor = scaleFactor;
    const x = clickX / effectiveScaleFactor;
    const y = clickY / effectiveScaleFactor;

    // Create new signature field (not locked)
    const newField = {
      id: Date.now().toString(),
      x: Math.max(0, x - 75), // Center the field on click position
      y: Math.max(0, y - 25),
      width: 150,
      height: 50,
      page: pageNumber,
      signatureData: null,
      signerName: '',
      isLocked: false
    };

    setNewSignatureFields(prev => [...prev, newField]);
    setActiveFieldId(newField.id);
    setSignatureModalOpen(true);
  };

  // Update a signature field (only new fields can be updated)
  const handleFieldUpdate = (updatedField) => {
    if (updatedField.isLocked) return; // Don't update locked fields
    setNewSignatureFields(prev =>
      prev.map(f => f.id === updatedField.id ? updatedField : f)
    );
  };

  // Remove a signature field (only new fields can be removed)
  const handleFieldRemove = (fieldId) => {
    // Only remove from new signature fields (not existing/locked ones)
    setNewSignatureFields(prev => prev.filter(f => f.id !== fieldId));
    if (activeFieldId === fieldId) {
      setActiveFieldId(null);
    }
  };

  // Handle field click - open modal if not signed and not locked
  const handleFieldClick = (field) => {
    if (field.isLocked) return; // Don't open modal for locked fields
    setActiveFieldId(field.id);
    if (!field.signatureData) {
      setSignatureModalOpen(true);
    }
  };

  const handleClearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
      setHasDrawnSignature(false);
    }
  };

  // Handle signature image upload
  const handleSignatureImageUpload = (file) => {
    const isImage = file.type === 'image/png' || file.type === 'image/jpeg';
    if (!isImage) {
      message.error('Only PNG and JPG images are allowed');
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB');
      return false;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedSignaturePreview(e.target.result);
    };
    reader.readAsDataURL(file);
    return false; // Prevent auto upload
  };

  // Clear uploaded signature
  const handleClearUploadedSignature = () => {
    setUploadedSignaturePreview(null);
  };

  // Reset modal state
  const resetModalState = () => {
    setSignatureMode('draw');
    setUploadedSignaturePreview(null);
    sigCanvasRef.current?.clear();
    setHasDrawnSignature(false);
    setSignerName('');
  };

  // Save signature to the active field (local state only)
  const handleSaveSignatureToField = () => {
    let signatureBase64;

    if (signatureMode === 'draw') {
      if (sigCanvasRef.current.isEmpty()) {
        message.warning("Please draw a signature first");
        return;
      }
      signatureBase64 = sigCanvasRef.current.toDataURL('image/png');
    } else {
      if (!uploadedSignaturePreview) {
        message.warning("Please upload a signature image first");
        return;
      }
      signatureBase64 = uploadedSignaturePreview;
    }

    // Only update new signature fields (not locked ones)
    setNewSignatureFields(prev => prev.map(field =>
      field.id === activeFieldId
        ? { ...field, signatureData: signatureBase64, signerName }
        : field
    ));

    // Cleanup
    setSignatureModalOpen(false);
    sigCanvasRef.current?.clear();
    setHasDrawnSignature(false);
    setSignerName('');
    setSignatureMode('draw');
    setUploadedSignaturePreview(null);
    message.success("Signature added to field");
  };

  // Submit new signatures to backend (existing signatures are already saved)
  const handleSubmitSignatures = async (attempt = 0) => {
    // Only submit new signatures that have been filled
    const filledNewFields = newSignatureFields.filter(f => f.signatureData);

    if (filledNewFields.length === 0) {
      message.warning("Please add at least one new signature by clicking on the document");
      return;
    }

    setSaving(true);
    try {
      const response = await actions.signDocument(id, {
        signatures: filledNewFields.map(field => ({
          signature_image_as_base64: field.signatureData,
          signer_name: field.signerName,
          page: field.page,
          position_x: field.x,
          position_y: field.y,
          width: field.width,
          height: field.height
        }))
      });

      if (response) {
        setSaving(false);
        Modal.success({
          title: 'Document Signed Successfully!',
          content: `${filledNewFields.length} new signature(s) have been applied to the document.`,
          okText: 'Go to Documents',
          onOk: () => navigate('/documents'),
        });
        return;
      }
    } catch (error) {
      // Retry logic with exponential backoff
      if (attempt < MAX_RETRIES) {
        const delay = Math.pow(2, attempt) * 1000;
        message.warning(`Signing failed. Retrying in ${delay / 1000} seconds... (Attempt ${attempt + 1}/${MAX_RETRIES})`);
        setTimeout(() => handleSubmitSignatures(attempt + 1), delay);
        return;
      } else {
        notify("error", "Failed to sign document", error?.response?.data?.message || "Please check your connection and try again");
      }
    }
    setSaving(false);
  };

  // Download signed PDF handler
  const handleDownloadSignedPdf = async () => {
    if (!document?.signed_document_file) {
      message.error("No signed document available");
      return;
    }

    const fileUrl = document.signed_document_file_url || document.signed_document_file;

    try {
      const response = await axios.get(fileUrl, {
        responseType: "blob",
      });

      if (response.status === 200) {
        const filename = `signed_${document.name || 'document'}.pdf`;
        fileDownload(response.data, filename);
        message.success("Download started");
      }
    } catch (err) {
      console.error("Download error:", err);
      message.error("Failed to download signed document. Please try again.");
    }
  };

  // Count signatures - separate existing from new
  const existingSignaturesCount = existingSignatures.length;
  const filledNewSignaturesCount = newSignatureFields.filter(f => f.signatureData).length;
  const totalNewFieldsCount = newSignatureFields.length;
  const totalAllSignatures = existingSignaturesCount + filledNewSignaturesCount;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <Card
        title={`Sign Document: ${document?.name || ''}`}
        extra={
          <Flex gap={10} align="center">
            {(existingSignaturesCount > 0 || totalNewFieldsCount > 0) && (
              <Tooltip title={existingSignaturesCount > 0 ? `${existingSignaturesCount} existing + ${filledNewSignaturesCount} new` : ''}>
                <Badge
                  count={`${totalAllSignatures}${totalNewFieldsCount > filledNewSignaturesCount ? ` (${totalNewFieldsCount - filledNewSignaturesCount} pending)` : ''}`}
                  style={{ backgroundColor: filledNewSignaturesCount === totalNewFieldsCount && totalNewFieldsCount > 0 ? '#52c41a' : '#1890ff' }}
                >
                  <span style={{ marginRight: 8 }}>Signatures</span>
                </Badge>
              </Tooltip>
            )}
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/documents')}>
              Back
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSubmitSignatures}
              disabled={filledNewSignaturesCount === 0}
              loading={saving}
            >
              {`Submit ${filledNewSignaturesCount > 0 ? `(${filledNewSignaturesCount} new)` : ''}`}
            </Button>
          </Flex>
        }
      >
        {document?.is_signed && (
          <Alert
            message={<span style={{ fontWeight: 600 }}>Document Previously Signed</span>}
            description={
              <div>
                <p style={{ marginBottom: 8 }}>
                  This document was signed on {document.signed_at
                    ? new Date(document.signed_at).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })
                    : 'an unknown date'
                  }
                  {document.signer_name ? ` by ${document.signer_name}` : ''}.
                  {' '}You can still add additional signatures.
                </p>

                {document.signed_document_file && (
                  <div style={{
                    marginTop: 12,
                    padding: 12,
                    backgroundColor: '#f6ffed',
                    borderRadius: 6,
                    border: '1px solid #b7eb8f'
                  }}>
                    <div style={{ fontWeight: 500, marginBottom: 8 }}>
                      Signed Document Available
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      onClick={handleDownloadSignedPdf}
                      icon={<DownloadOutlined />}
                    >
                      Download Signed PDF
                    </Button>
                  </div>
                )}

                {existingSignaturesCount > 0 && (
                  <div style={{ marginTop: 12, color: '#722ed1' }}>
                    <LockOutlined style={{ marginRight: 4 }} />
                    {existingSignaturesCount} existing signature{existingSignaturesCount > 1 ? 's' : ''} loaded (locked)
                  </div>
                )}
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Alert
          message="Click anywhere on the PDF to add a signature field"
          description="You can add multiple signatures, drag them to reposition, and resize them. After adding signatures, click Submit to save."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* PDF Preview */}
        <div
          ref={pdfContainerRef}
          style={{
            backgroundColor: '#666',
            padding: 20,
            borderRadius: 8,
            maxHeight: 'calc(100vh - 400px)',
            overflowY: 'auto'
          }}
        >
          <Flex justify="center">
            {pdfError ? (
              <div style={{ color: '#fff', padding: 40, textAlign: 'center' }}>
                <p>Failed to load PDF</p>
                <Button type="primary" onClick={fetchDocument} style={{ marginTop: 10 }}>
                  Retry
                </Button>
              </div>
            ) : pdfUrl ? (
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<Spin size="large" tip="Loading PDF..." />}
                error={null}
              >
                {numPages > 0 && [...Array(numPages).keys()].map((_, index) => {
                  const pageNumber = index + 1;
                  const pageFields = allSignatureFields.filter(f => f.page === pageNumber);

                  return (
                    <div
                      key={pageNumber}
                      style={{
                        position: 'relative',
                        marginBottom: 10,
                        cursor: 'crosshair'
                      }}
                    >
                      <div
                        onClick={(e) => handlePdfClick(e, pageNumber)}
                        style={{ position: 'relative' }}
                      >
                        <Page
                          pageNumber={pageNumber}
                          scale={zoomLevel}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />

                        {/* Signature field overlays */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          pointerEvents: 'none'
                        }}>
                          {pageFields.map(field => (
                            <div
                              key={field.id}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                pointerEvents: 'auto'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <SignatureField
                                field={field}
                                scaleFactor={scaleFactor}
                                onUpdate={handleFieldUpdate}
                                onRemove={handleFieldRemove}
                                onClick={() => handleFieldClick(field)}
                                isActive={activeFieldId === field.id}
                                disabled={false}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      <div style={{ textAlign: 'center', backgroundColor: '#fff', padding: '5px' }}>
                        Page {pageNumber} {pageFields.length > 0 && `(${pageFields.length} signature${pageFields.length > 1 ? 's' : ''})`}
                      </div>
                    </div>
                  );
                })}
              </Document>
            ) : (
              <div style={{ color: '#fff', padding: 40 }}>
                No PDF preview available
              </div>
            )}
          </Flex>
        </div>

        {/* Page Navigation */}
        <Flex justify="center" style={{ marginTop: 16 }} gap={16} align="center">
          <Button onClick={() => setZoomLevel(prev => Math.max(0.5, prev - 0.25))}>
            Zoom Out
          </Button>
          <span>
            {numPages} page{numPages !== 1 ? 's' : ''} | Zoom: {Math.round(zoomLevel * 100)}%
          </span>
          <Button onClick={() => setZoomLevel(prev => Math.min(2, prev + 0.25))}>
            Zoom In
          </Button>
        </Flex>

        {/* Signature summary */}
        {(existingSignaturesCount > 0 || totalNewFieldsCount > 0) && (
          <Flex justify="center" style={{ marginTop: 10 }}>
            <Alert
              message={
                existingSignaturesCount > 0
                  ? `${existingSignaturesCount} existing + ${filledNewSignaturesCount} of ${totalNewFieldsCount} new signature(s)`
                  : `${filledNewSignaturesCount} of ${totalNewFieldsCount} signature field(s) filled`
              }
              description={
                filledNewSignaturesCount < totalNewFieldsCount
                  ? "Click on empty signature fields to add your signature"
                  : totalNewFieldsCount > 0
                    ? "All new signature fields are filled. Click Submit to sign the document."
                    : existingSignaturesCount > 0
                      ? "Click anywhere on the PDF to add new signatures."
                      : null
              }
              type={filledNewSignaturesCount === totalNewFieldsCount && totalNewFieldsCount > 0 ? "success" : "info"}
              showIcon
            />
          </Flex>
        )}
      </Card>

      {/* Signature Modal */}
      <Modal
        title="Add Your Signature"
        open={signatureModalOpen}
        onCancel={() => {
          resetModalState();
          setSignatureModalOpen(false);
          // Remove the field if it was just created and has no signature
          const activeField = newSignatureFields.find(f => f.id === activeFieldId);
          if (activeField && !activeField.signatureData) {
            handleFieldRemove(activeFieldId);
          }
        }}
        width={600}
        footer={[
          <Button
            key="clear"
            icon={<UndoOutlined />}
            onClick={signatureMode === 'draw' ? handleClearSignature : handleClearUploadedSignature}
          >
            Clear
          </Button>,
          <Button key="cancel" onClick={() => {
            resetModalState();
            setSignatureModalOpen(false);
            const activeField = newSignatureFields.find(f => f.id === activeFieldId);
            if (activeField && !activeField.signatureData) {
              handleFieldRemove(activeFieldId);
            }
          }}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSaveSignatureToField}
            disabled={signatureMode === 'draw' ? !hasDrawnSignature : !uploadedSignaturePreview}
          >
            Add Signature
          </Button>
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <label>Your Name (optional):</label>
          <input
            type="text"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            placeholder="Enter your name"
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #d9d9d9',
              marginTop: '4px'
            }}
          />
        </div>

        <Tabs
          activeKey={signatureMode}
          onChange={(key) => setSignatureMode(key)}
          items={[
            {
              key: 'draw',
              label: 'Draw Signature',
              children: (
                <>
                  <p>Draw your signature in the box below:</p>
                  <div style={{
                    border: hasDrawnSignature ? '2px solid #52c41a' : '2px solid #d9d9d9',
                    borderRadius: 8,
                    backgroundColor: '#fff',
                    transition: 'border-color 0.3s'
                  }}>
                    <SignatureCanvas
                      ref={sigCanvasRef}
                      onEnd={() => setHasDrawnSignature(true)}
                      canvasProps={{
                        width: 550,
                        height: 200,
                        className: 'signature-canvas',
                        style: { borderRadius: '6px' }
                      }}
                      penColor="black"
                    />
                  </div>
                  {hasDrawnSignature && (
                    <div style={{ color: '#52c41a', marginTop: 4, fontSize: 12 }}>
                      Signature captured
                    </div>
                  )}
                </>
              )
            },
            {
              key: 'upload',
              label: 'Upload Image',
              children: (
                <>
                  <p>Upload a signature image (PNG or JPG, max 2MB):</p>
                  <Upload
                    accept=".png,.jpg,.jpeg"
                    beforeUpload={handleSignatureImageUpload}
                    showUploadList={false}
                    maxCount={1}
                  >
                    <Button icon={<UploadOutlined />}>
                      {uploadedSignaturePreview ? 'Change Image' : 'Select Image'}
                    </Button>
                  </Upload>
                  {uploadedSignaturePreview && (
                    <div style={{
                      marginTop: 16,
                      border: '2px solid #52c41a',
                      borderRadius: 8,
                      padding: 10,
                      backgroundColor: '#fff'
                    }}>
                      <p style={{ marginBottom: 8, color: '#52c41a', fontSize: 12 }}>
                        Preview:
                      </p>
                      <img
                        src={uploadedSignaturePreview}
                        alt="Signature preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: 200,
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  )}
                </>
              )
            }
          ]}
        />

        <Divider />

        <p style={{ color: '#666', fontSize: 12 }}>
          By signing this document, you agree to the terms and conditions contained within.
          You can add multiple signatures by clicking on different locations on the PDF.
        </p>
      </Modal>
    </div>
  );
};

export default DocumentSigningPage;
